/**
 * Practices Home — three core daily-use experiences
 *   1. Resets        → /practices/resets
 *   2. State Tuning  → /practices/state-tuning
 *   3. Tapping       → /practices/tapping
 *
 * Spec: deep navy → soft gradient, 5–8% amber overlay, slow breathing motion,
 * ritual space (NOT a dashboard), no clinical UI.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CARDS = [
  {
    id: "resets",
    title: "Resets",
    sub: "Come back in seconds",
    to: "/practices/resets",
    glow: "rgba(196,170,116,0.18)",
    accent: "rgba(217,180,120,0.55)",
  },
  {
    id: "state-tuning",
    title: "State Tuning",
    sub: "Shift your state",
    to: "/practices/state-tuning",
    glow: "rgba(160,180,220,0.22)",
    accent: "rgba(190,205,235,0.55)",
  },
  {
    id: "tapping",
    title: "Tapping",
    sub: "Release what's in the way",
    to: "/practices/tapping",
    glow: "rgba(190,165,205,0.22)",
    accent: "rgba(215,195,225,0.55)",
  },
];

export default function PracticesHome() {
  const navigate = useNavigate();
  const [arrived, setArrived] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setArrived(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      data-testid="practices-home"
      style={{
        background:
          "radial-gradient(ellipse at 50% 18%, #0E1A2C 0%, #0A1424 32%, #060B16 68%, #03060D 100%)",
      }}
    >
      {/* 5–8% amber overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(217,165,90,0.06) 0%, rgba(217,165,90,0.02) 40%, transparent 75%)",
        }}
      />
      {/* Slow atmospheric drift */}
      <motion.div
        aria-hidden
        animate={{ opacity: [0.4, 0.65, 0.4] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 70% 30%, rgba(150,180,220,0.06), transparent 55%)",
        }}
      />
      {/* Star-noise texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Header */}
      <header className="relative z-10 px-5 pt-5 pb-2 flex items-center">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="p-2 -ml-2 rounded-lg hover:bg-white/[0.04] transition-colors"
          data-testid="practices-back-btn"
          aria-label="Back"
        >
          <ArrowLeft size={20} className="text-white/55" />
        </button>
      </header>

      {/* Title */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 12 }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 px-6 pt-8 pb-12 text-center"
      >
        <h1
          className="text-white/95 text-4xl sm:text-5xl tracking-tight"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400 }}
        >
          Practices
        </h1>
        <p
          className="mt-3 text-white/45 text-sm tracking-[0.18em] uppercase"
          style={{ letterSpacing: "0.18em" }}
        >
          Return. Reset. Re-centre.
        </p>
      </motion.section>

      {/* Cards stack */}
      <main className="relative z-10 px-5 pb-32 space-y-5 max-w-md mx-auto">
        {CARDS.map((card, i) => (
          <motion.button
            key={card.id}
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 22 }}
            transition={{ duration: 1.1, delay: 0.25 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ scale: 1.012 }}
            whileTap={{ scale: 0.985 }}
            onClick={() => navigate(card.to)}
            data-testid={`practices-card-${card.id}`}
            className="w-full block text-left rounded-3xl px-7 py-9 relative overflow-hidden group"
            style={{
              background:
                "linear-gradient(140deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.015) 60%, rgba(255,255,255,0.025) 100%)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              border: "1px solid rgba(255,255,255,0.06)",
              boxShadow: `0 22px 60px -28px ${card.glow}, inset 0 1px 0 rgba(255,255,255,0.03)`,
            }}
          >
            {/* Slow breathing glow inside the card */}
            <motion.div
              aria-hidden
              animate={{ opacity: [0.4, 0.85, 0.4], scale: [1, 1.05, 1] }}
              transition={{ duration: 7 + i * 0.7, repeat: Infinity, ease: "easeInOut" }}
              className="pointer-events-none absolute -right-12 -top-12 w-44 h-44 rounded-full"
              style={{
                background: `radial-gradient(circle, ${card.glow} 0%, transparent 70%)`,
                filter: "blur(8px)",
              }}
            />
            <div className="relative">
              <h3
                className="text-white/90 text-2xl"
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 400,
                  letterSpacing: "-0.005em",
                }}
              >
                {card.title}
              </h3>
              <p className="mt-1.5 text-white/50 text-sm" style={{ fontWeight: 300 }}>
                {card.sub}
              </p>
              {/* Hairline accent — appears on hover */}
              <div
                aria-hidden
                className="mt-5 h-px transition-all duration-700"
                style={{
                  background: `linear-gradient(90deg, transparent, ${card.accent}, transparent)`,
                  opacity: 0.55,
                }}
              />
            </div>
          </motion.button>
        ))}

        {/* Anchor copy */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: arrived ? 1 : 0 }}
          transition={{ duration: 1.4, delay: 1.05 }}
          className="text-center text-white/30 text-xs tracking-[0.25em] uppercase pt-6"
        >
          Use any. Use daily.
        </motion.p>
      </main>
    </div>
  );
}
